export const enum ClientOp {
  Hello = 0x01,
  JoinQueue = 0x02,
  Input = 0x03,
  Leave = 0x04,
  Ping = 0x05,
}

export const enum ServerOp {
  JoinedRoom = 0x10,
  Snapshot = 0x11,
  EventEat = 0x12,
  EventKill = 0x13,
  Result = 0x14,
  Error = 0x15,
  Pong = 0x16,
}

export interface PlayerState {
  id: number;
  x: number;
  z: number;
  size: number;
  score: number;
}

export interface ItemState {
  id: number;
  kind: number;
  x: number;
  z: number;
  size: number;
}

export type ServerMessage =
  | { op: ServerOp.JoinedRoom; roomId: string; mapId: string; roomSize: number; duration: number; playerId: number; players: PlayerState[] }
  | { op: ServerOp.Snapshot; tick: number; players: PlayerState[]; items: ItemState[] }
  | { op: ServerOp.EventEat; playerId: number; itemId: number }
  | { op: ServerOp.EventKill; killerId: number; victimId: number }
  | { op: ServerOp.Result; rank: number; scoreDelta: number; coinsDelta: number; totalScore: number }
  | { op: ServerOp.Error; code: number; message: string }
  | { op: ServerOp.Pong; sequence: number };

class Writer {
  private bytes: number[] = [];
  u8(value: number) { this.bytes.push(value & 0xff); }
  u32(value: number) {
    const v = value >>> 0;
    this.bytes.push(v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff);
  }
  f32(value: number) {
    const buffer = new ArrayBuffer(4);
    new DataView(buffer).setFloat32(0, value, true);
    this.bytes.push(...new Uint8Array(buffer));
  }
  str(value: string) {
    const encoded = new TextEncoder().encode(value);
    if (encoded.length > 255) throw new Error("Protocol string exceeds 255 bytes");
    this.u8(encoded.length);
    this.bytes.push(...encoded);
  }
  finish() { return new Uint8Array(this.bytes); }
}

class Reader {
  private readonly view: DataView;
  private readonly bytes: Uint8Array;
  private offset = 0;

  constructor(input: ArrayBuffer | Uint8Array) {
    this.bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
    this.view = new DataView(this.bytes.buffer, this.bytes.byteOffset, this.bytes.byteLength);
  }
  private ensure(length: number) {
    if (this.offset + length > this.view.byteLength) throw new Error("Truncated arena frame");
  }
  u8() { this.ensure(1); return this.view.getUint8(this.offset++); }
  u16() { this.ensure(2); const value = this.view.getUint16(this.offset, true); this.offset += 2; return value; }
  u32() { this.ensure(4); const value = this.view.getUint32(this.offset, true); this.offset += 4; return value; }
  i32() { this.ensure(4); const value = this.view.getInt32(this.offset, true); this.offset += 4; return value; }
  f32() { this.ensure(4); const value = this.view.getFloat32(this.offset, true); this.offset += 4; return value; }
  str() {
    const length = this.u8();
    this.ensure(length);
    const value = new TextDecoder().decode(this.bytes.subarray(this.offset, this.offset + length));
    this.offset += length;
    return value;
  }
}

export function encodeHello(deviceId: string, token = "", region = "eu-west"): Uint8Array {
  const writer = new Writer();
  writer.u8(ClientOp.Hello);
  writer.u8(1);
  writer.str(region);
  writer.str(deviceId);
  writer.str(token);
  return writer.finish();
}

export function encodeJoinQueue(): Uint8Array {
  return new Uint8Array([ClientOp.JoinQueue, 1]);
}

export function encodeInput(sequence: number, x: number, z: number): Uint8Array {
  const writer = new Writer();
  writer.u8(ClientOp.Input);
  writer.u32(sequence);
  writer.f32(x);
  writer.f32(z);
  return writer.finish();
}

export function encodePing(sequence: number): Uint8Array {
  const writer = new Writer();
  writer.u8(ClientOp.Ping);
  writer.u32(sequence);
  return writer.finish();
}

export function encodeLeave(): Uint8Array {
  return new Uint8Array([ClientOp.Leave]);
}

function readPlayers(reader: Reader, count: number): PlayerState[] {
  const players: PlayerState[] = [];
  for (let index = 0; index < count; index++) {
    players.push({ id: reader.u16(), x: reader.f32(), z: reader.f32(), size: reader.f32(), score: reader.u32() });
  }
  return players;
}

export function decodeServerMessage(input: ArrayBuffer | Uint8Array): ServerMessage {
  const reader = new Reader(input);
  const op = reader.u8() as ServerOp;
  switch (op) {
    case ServerOp.JoinedRoom: {
      const roomId = reader.str();
      const mapId = reader.str();
      const roomSize = reader.u8();
      const duration = reader.u16();
      const playerId = reader.u16();
      return { op, roomId, mapId, roomSize, duration, playerId, players: readPlayers(reader, reader.u8()) };
    }
    case ServerOp.Snapshot: {
      const tick = reader.u32();
      const players = readPlayers(reader, reader.u8());
      const itemCount = reader.u16();
      const items: ItemState[] = [];
      for (let index = 0; index < itemCount; index++) {
        items.push({ id: reader.u16(), kind: reader.u8(), x: reader.f32(), z: reader.f32(), size: reader.f32() });
      }
      return { op, tick, players, items };
    }
    case ServerOp.EventEat:
      return { op, playerId: reader.u16(), itemId: reader.u16() };
    case ServerOp.EventKill:
      return { op, killerId: reader.u16(), victimId: reader.u16() };
    case ServerOp.Result:
      return { op, rank: reader.u16(), scoreDelta: reader.i32(), coinsDelta: reader.i32(), totalScore: reader.u32() };
    case ServerOp.Error:
      return { op, code: reader.u8(), message: reader.str() };
    case ServerOp.Pong:
      return { op, sequence: reader.u32() };
    default:
      throw new Error(`Unknown arena opcode ${op}`);
  }
}
